import type { ListBuilderConfiguration } from '../types';

/**
 * License Discrepancies embedded list configuration.
 *
 * Drives the Discrepancies tab on the license-detail page. Data source:
 * `GET /api/licenses/:id/discrepancies`. Filters from
 * `libs/core/src/filters/config/license-discrepancies.ts`:
 *   - `discFieldName`     — multi-select Status / Expiration / License # / Name
 *   - `discSnoozeState`   — radio Active alerts / Snoozed / All (default Active)
 *
 * The row count is bounded at four per license (one per signal), so
 * pagination is mostly cosmetic — the backend service still honours the
 * standard list-builder envelope for symmetry with other detail tabs.
 *
 * Per-row Snooze / Unsnooze is wired through the `actions_dropdown`
 * column. The tab component supplies the callbacks; visibility is
 * derived from row data — only soft + active rows can be snoozed,
 * only soft + snoozed rows can be unsnoozed.
 */
export const LICENSE_DISCREPANCIES_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-license-discrepancies-embedded',
			cacheTTL: 60000,
			name: 'Discrepancies',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				// Severity column was removed — "soft / hard" is an internal
				// algorithm distinction that doesn't help end users. The
				// snoozable behavior is already conveyed by the per-row
				// action visibility (Snooze only shown on snoozable rows).
				{
					id: 'issue-column',
					fields: [
						{
							// Human-readable label populated server-side via
							// DISCREPANCY_FIELD_LABEL ("License Number
							// Mismatch", "Name Mismatch", "Expiration
							// Mismatch", "License Status Alert"). Replaces
							// the previous raw `field` column which surfaced
							// internal field names like "licenseNumber" /
							// "expirationDate" to end users.
							id: 'issue-label',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'issue',
									objectTable: 'license_discrepancy',
								},
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-issue', text: 'Issue' }],
					sortable: false,
					width: 220,
				},
				{
					id: 'stored-column',
					fields: [
						{
							id: 'stored-value',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'stored',
									objectTable: 'license_discrepancy',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-stored', text: 'Stored (CRM)' }],
					sortable: false,
					minWidth: 180,
				},
				{
					id: 'registry-column',
					fields: [
						{
							id: 'registry-value',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'registry',
									objectTable: 'license_discrepancy',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-registry', text: 'State Registry' }],
					sortable: false,
					minWidth: 180,
				},
				{
					// `snoozeState` underneath is `'active' | 'snoozed'` —
					// "Status" reads more naturally to users than "State".
					id: 'snooze-status-column',
					fields: [
						{
							id: 'snooze-status-badge',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'snoozeState',
									objectTable: 'license_discrepancy',
								},
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								// F9 + F40: title-case the displayed label via
								// valueMapping displayValue (now an engine
								// alias for displayText post F40). Color
								// comes from the status_map colorRule.
								valueMapping: [
									{ value: 'active', displayValue: 'Active' },
									{ value: 'snoozed', displayValue: 'Snoozed' },
								],
								colorRule: {
									type: 'status_map',
									statusMap: {
										active: 'warning',
										snoozed: 'default',
									},
								},
							},
						},
					],
					headers: [{ id: 'h-snooze-status', text: 'Status' }],
					sortable: false,
					width: 110,
				},
				{
					id: 'snoozed-by-column',
					fields: [
						{
							id: 'snoozed-by',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'snoozedByUserName',
									objectTable: 'license_discrepancy',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'snoozed-by-break',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'snoozed-at',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'snoozedAt',
									objectTable: 'license_discrepancy',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { dateFormat: 'mdy' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-snoozed-by', text: 'Snoozed By' }],
					sortable: false,
					minWidth: 140,
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							// F7 (revised): Snooze / Remove Snooze are
							// rendered inline as labeled buttons (icon + text)
							// so the rep can see what the action does without
							// hovering or opening an overflow menu. The
							// previous overflow-menu approach hid the action
							// behind a three-dot button — too easy to miss for
							// a workflow this central to the tab. `showLabel`
							// is honoured by ActionsDropdownDisplay only for
							// non-overflow entries; per-row visibility still
							// flows through `visibilityCallback`.
							dropdownActions: [
								{
									id: 'snooze',
									label: 'Snooze',
									iconName: 'faBellSlash',
									callbackName: 'snoozeDiscrepancy',
									// Row-level visibility: tab supplies a
									// `canSnoozeRow` predicate that gates on
									// snoozable + active.
									visibilityCallback: 'canSnoozeRow',
									visibility: 'visible',
									showLabel: true,
								},
								{
									id: 'unsnooze',
									label: 'Remove Snooze',
									iconName: 'faBell',
									callbackName: 'unsnoozeDiscrepancy',
									visibilityCallback: 'canUnsnoozeRow',
									visibility: 'visible',
									showLabel: true,
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					sortable: false,
					width: 170,
				},
			],
			tableSorts: [],
			defaultSortId: undefined,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50],
	},
	showResetButton: true,
	filters: {
		activeFilters: ['discFieldName', 'discSnoozeState'],
	},
	objects: [
		{
			baseUrl: '/licenses/:id/discrepancies',
			objectLabel: 'Discrepancy',
			objectTable: 'license_discrepancy',
			selectedFields: [
				{ fieldName: 'issue', fieldLabel: 'Issue' },
				{ fieldName: 'stored', fieldLabel: 'Stored (CRM)' },
				{ fieldName: 'registry', fieldLabel: 'State Registry' },
				{ fieldName: 'snoozeState', fieldLabel: 'Status' },
				{ fieldName: 'snoozedAt', fieldLabel: 'Snoozed At' },
				{ fieldName: 'snoozedByUserName', fieldLabel: 'Snoozed By' },
			],
		},
	],
};
